import React, { useEffect, useState } from 'react';
import { Card, Statistic, Row, Col, Table, Tag, message, Popconfirm, Button } from 'antd';
import { ClockCircleOutlined, UserOutlined, ExclamationCircleOutlined, CheckOutlined, CloseOutlined } from '@ant-design/icons';
import { appointmentApi } from '../api/appointment';
import { Appointment, AppointmentStatus } from '../api/model';
import dayjs from 'dayjs';

// 定义表格数据的接口
interface TableDataItem {
  key: string;
  name: string;
  gender: string;
  age: string | number;
  condition: string;
  appointmentTime: string;
  status: AppointmentStatus;
  location: string;
}

// 状态标签配置
const statusTag = (status: AppointmentStatus) => {
  switch (status) {
    case AppointmentStatus.Confirmed:
      return <Tag color="blue">已确认</Tag>;
    case AppointmentStatus.Pending:
      return <Tag color="orange">待确认</Tag>;
    case AppointmentStatus.Rejected:
      return <Tag color="gray">已拒绝</Tag>;
    case AppointmentStatus.Cancelled:
      return <Tag color="gray">已取消</Tag>;
    default:
      return <Tag color="gray">未知</Tag>;
  }
};

// 计算年龄
const calculateAge = (birthDate: string | null) => {
  if (!birthDate) return '未知';
  const birth = dayjs(birthDate);
  const today = dayjs();
  return today.diff(birth, 'year');
};

const Dashboard: React.FC = () => {
  const [todayAppointments, setTodayAppointments] = useState(0);
  const [pendingAppointments, setPendingAppointments] = useState(0);
  const [weekAppointments, setWeekAppointments] = useState(0);
  const [pendingConfirmationCount, setPendingConfirmationCount] = useState(0);
  const [todayAppointmentsData, setTodayAppointmentsData] = useState<TableDataItem[]>([]);
  const [pendingAppointmentsData, setPendingAppointmentsData] = useState<TableDataItem[]>([]);
  const [loading, setLoading] = useState(true);

  // 获取统计数据
  const fetchStatistics = async () => {
    try {
      const now = dayjs();
      const startOfDay = now.startOf('day').toISOString();
      const endOfDay = now.endOf('day').toISOString();
      console.log(now, now.startOf('week'));
      // 获取本周开始和结束时间
      const startOfWeek = now.startOf('week').toISOString();
      const endOfWeek = now.endOf('week').toISOString();
      
      // 获取今日内所有Confirmed或Pending的预约
      const todayResponse = await appointmentApi.listAppointments({
        start_time: startOfDay,
        end_time: endOfDay,
        status_list: [AppointmentStatus.Confirmed, AppointmentStatus.Pending]
      });
      
      // 获取今日及以后的所有Pending预约
      const pendingResponse = await appointmentApi.listAppointments({
        start_time: startOfDay,
        status_list: [AppointmentStatus.Pending]
      });
      
      if (todayResponse.success) {
        const appointments = todayResponse.data;
        
        // 今日接诊数量：今日内、start time在当前时间以前的状态为Confirmed或Pending的预约数量
        const todayAppointmentsCount = appointments.filter(app => 
          dayjs(app.start_time) <= dayjs() && 
          (app.status === AppointmentStatus.Confirmed || app.status === AppointmentStatus.Pending)
        ).length;
        setTodayAppointments(todayAppointmentsCount);
        
        // 待诊患者数量：今日内、start time在当前时间之后的状态为Confirmed或Pending的预约数量
        const pendingAppointmentsCount = appointments.filter(app => 
          dayjs(app.start_time) > dayjs() && 
          (app.status === AppointmentStatus.Confirmed || app.status === AppointmentStatus.Pending)
        ).length;
        setPendingAppointments(pendingAppointmentsCount);
      }
      
      // 使用新的pendingResponse来更新待确认预约列表数据
      if (pendingResponse.success) {
        const pendingAppointmentsFormatted = pendingResponse.data
          .map((appointment: Appointment) => ({
            key: appointment.id,
            name: appointment.patient.user?.name || '未知',
            gender: appointment.patient.gender === 'male' ? '男' : appointment.patient.gender === 'female' ? '女' : '未知',
            age: calculateAge(appointment.patient.birth_date),
            condition: appointment.description || '无',
            appointmentTime: dayjs(appointment.start_time).format('M月D日 HH:mm') + ' (' + dayjs(appointment.start_time).fromNow() + ')',
            status: appointment.status,
            location: appointment.schedule_location?.name || '未指定'
          }));
        setPendingAppointmentsData(pendingAppointmentsFormatted);
        
        // 更新待确认预约数量
        setPendingConfirmationCount(pendingResponse.data.length);
      }
      
      // 获取本周内、start time在当前时间以前的状态为Confirmed或Pending的预约数量
      const weekResponse = await appointmentApi.listAppointments({
        start_time: startOfWeek,
        end_time: endOfWeek,
        status_list: [AppointmentStatus.Confirmed, AppointmentStatus.Pending]
      });
      
      if (weekResponse.success) {
        const weekAppointmentsCount = weekResponse.data.filter(app => 
          dayjs(app.start_time) <= dayjs() && 
          (app.status === AppointmentStatus.Confirmed || app.status === AppointmentStatus.Pending)
        ).length;
        setWeekAppointments(weekAppointmentsCount);
      }
      
      // 获取今日内所有状态为Confirmed或Pending的预约并在前端格式化显示
      if (todayResponse.success) {
        const formattedData = todayResponse.data
          .filter(app => app.status === AppointmentStatus.Confirmed || app.status === AppointmentStatus.Pending)
          .map((appointment: Appointment) => ({
            key: appointment.id,
            name: appointment.patient.user?.name || '未知',
            gender: appointment.patient.gender === 'male' ? '男' : appointment.patient.gender === 'female' ? '女' : '未知',
            age: calculateAge(appointment.patient.birth_date),
            condition: appointment.description || '无',
            appointmentTime: dayjs(appointment.start_time).format('HH:mm'),
            status: appointment.status,
            location: appointment.schedule_location?.name || '未指定'
          }));
        setTodayAppointmentsData(formattedData);
      }
    } catch (error) {
      console.error('获取统计数据失败:', error);
      message.error('获取统计数据失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatistics();
  }, []);

  // 确认预约
  const handleConfirm = async (id: string) => {
    try {
      const response = await appointmentApi.confirmAppointment(id);
      if (response.success) {
        message.success('预约确认成功');
        // 重新获取统计数据以更新界面
        fetchStatistics();
      } else {
        message.error(response.message || '预约确认失败');
      }
    } catch (error) {
      console.error('确认预约失败:', error);
      message.error('确认预约失败');
    }
  };

  // 拒绝预约
  const handleReject = async (id: string) => {
    try {
      const response = await appointmentApi.rejectAppointment(id);
      if (response.success) {
        message.success('预约拒绝成功');
        // 重新获取统计数据以更新界面
        fetchStatistics();
      } else {
        message.error(response.message || '预约拒绝失败');
      }
    } catch (error) {
      console.error('拒绝预约失败:', error);
      message.error('拒绝预约失败');
    }
  };

  const pendingAppointmentColumns = [
    { title: '姓名', dataIndex: 'name', key: 'name' },
    { title: '性别', dataIndex: 'gender', key: 'gender' },
    { title: '年龄', dataIndex: 'age', key: 'age' },
    { title: '预约说明', dataIndex: 'condition', key: 'condition' },
    { title: '预约时间', dataIndex: 'appointmentTime', key: 'appointmentTime' },
    { title: '预约地点', dataIndex: 'location', key: 'location' },
    {
      title: '操作',
      key: 'action',
      render: (record: TableDataItem) => (
        <>
          <Popconfirm
            title="确认预约"
            description="确定要接受此预约吗？"
            onConfirm={() => handleConfirm(record.key)}
            okText="确认"
            cancelText="取消"
          >
            <Button type="link" size="small" icon={<CheckOutlined />} >确认</Button>
          </Popconfirm>
          <Popconfirm
            title="拒绝预约"
            description="确定要拒绝此预约吗？"
            onConfirm={() => handleReject(record.key)}
            okText="确认"
            cancelText="取消"
          >
            <Button type="link" danger size="small" icon={<CloseOutlined />} style={{ marginLeft: 8 }} >拒绝</Button>
          </Popconfirm>
        </>
      ),
    },
  ];

  // 今日预约患者列表的列定义（包含状态列，不包含操作列）
  const todayAppointmentsColumns = [
    { title: '姓名', dataIndex: 'name', key: 'name' },
    { title: '性别', dataIndex: 'gender', key: 'gender' },
    { title: '年龄', dataIndex: 'age', key: 'age' },
    { title: '预约说明', dataIndex: 'condition', key: 'condition' },
    { title: '预约时间', dataIndex: 'appointmentTime', key: 'appointmentTime' },
    { title: '预约地点', dataIndex: 'location', key: 'location' },
    { 
      title: '状态', 
      dataIndex: 'status', 
      key: 'status',
      render: (status: AppointmentStatus) => statusTag(status)
    },
  ];

  return (
    <div>
      <h2>医生工作台</h2>
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={12} sm={6} lg={6}>
          <Card>
            <Statistic
              title="待确认预约数量"
              value={pendingConfirmationCount}
              precision={0}
              valueStyle={{ color: '#fa8c16' }}
              prefix={<ExclamationCircleOutlined />}
              suffix="人"
            />
          </Card>
        </Col>
        <Col xs={12} sm={6} lg={6}>
          <Card>
            <Statistic
              title="待诊患者"
              value={pendingAppointments}
              precision={0}
              valueStyle={{ color: '#cf1322' }}
              prefix={<ClockCircleOutlined />}
              suffix="人"
            />
          </Card>
        </Col>
        <Col xs={12} sm={6} lg={6}>
          <Card>
            <Statistic
              title="今日接诊"
              value={todayAppointments}
              precision={0}
              valueStyle={{ color: '#3f8600' }}
              prefix={<UserOutlined />}
              suffix="人"
            />
          </Card>
        </Col>
        <Col xs={12} sm={6} lg={6}>
          <Card>
            <Statistic
              title="本周接诊"
              value={weekAppointments}
              precision={0}
              valueStyle={{ color: '#1890ff' }}
              prefix={<UserOutlined />}
              suffix="人"
            />
          </Card>
        </Col>
      </Row>

      <Card title="待确认预约列表" loading={loading} style={{ marginBottom: 24 }}>
        <Table columns={pendingAppointmentColumns} dataSource={pendingAppointmentsData} pagination={false} />
      </Card>

      <Card title="今日预约患者列表" loading={loading}>
        <Table columns={todayAppointmentsColumns} dataSource={todayAppointmentsData} pagination={false} />
      </Card>
    </div>
  );
};

export default Dashboard;